/**
 * The module's first-run action: seed the staffing model from the CRM.
 *
 * WHY THIS EXISTS AS A BUTTON. The "no staffing model yet" card already told a
 * reader exactly what had to happen — "someone with manage:staffing can seed a
 * starting model from the current CRM book" — and gave them no way to do it. The
 * only route to a working module was a POST fired by hand, which on a deployed
 * environment means somebody pasting a bearer token into a console. A first-run
 * setup step belongs in the product, on the screen that names it.
 *
 * IT REPORTS WHAT HAPPENED, not that it succeeded. Bootstrapping is additive and
 * repeatable — cm-api creates the config only when none exists and the seats only
 * when that version has none — so the outcomes worth seeing are the partial ones:
 * a config that was already there, seats already present, advisors whose identity
 * could not be resolved. A green tick would hide every one of them.
 *
 * TWO THINGS IT DELIBERATELY DOES NOT DO, both surfaced afterwards rather than
 * left for someone to discover: it does not seed PINS (they are keyed by customer
 * name in the source and resolving them needs the #1802 reconciliation), and it
 * cannot know who the MANAGER is, because that is not derivable from customer
 * ownership — a manager may own nothing.
 */
import { useState } from 'react';
import { Button } from '@andavo/web-ui';
import { useMe } from '@/features/crm/auth/use-capabilities';
import { useStaffingData } from '../StaffingDataProvider';
import {
  bootstrapStaffing,
  StaffingWriteError,
  type BootstrapReport,
} from '../mutations';

export function SeedStaffingModel() {
  const { can } = useMe();
  const { reload } = useStaffingData();
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState<BootstrapReport | null>(null);
  const [error, setError] = useState<StaffingWriteError | null>(null);

  // BOTH authorities, because cm-api requires both and refuses with a 403
  // otherwise. Offering a button that always fails is worse than not offering
  // one: it reads as a broken feature rather than as a permission the caller
  // does not hold.
  const allowed = can('manage:staffing') && can('view:staffing_financials');

  if (!allowed) {
    return (
      <p
        className="text-content-secondary text-sm"
        data-testid="staffing-seed-denied"
      >
        Seeding the model needs <code>manage:staffing</code> and{' '}
        <code>view:staffing_financials</code> — it creates a configuration
        version, and a configuration carries the cost model.
      </p>
    );
  }

  const seed = async () => {
    setBusy(true);
    setError(null);
    try {
      // DELIBERATELY NOT reloading here. The read set IS stale the moment this
      // returns — but reloading flips the module to configured, which unmounts
      // this whole card and takes the report with it. Seeding then looks like a
      // page that blinked, and the identity warnings — the one part worth
      // reading — are gone before anyone can read them. Found by clicking the
      // button rather than by a test: the unit tests render this component
      // outside the state machine, so the report survived there.
      setReport(await bootstrapStaffing());
    } catch (err) {
      setError(
        err instanceof StaffingWriteError
          ? err
          : new StaffingWriteError('Could not seed the staffing model.', 0),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-3" data-testid="staffing-seed">
      <div className="flex flex-wrap items-center gap-2">
        <Button disabled={busy} onClick={seed} size="sm">
          {busy ? 'Seeding from the CRM…' : 'Seed the model from the CRM'}
        </Button>
        <span className="text-content-secondary text-xs">
          Creates the roster, a first configuration and today’s team. It changes
          no customer record, and running it again does not overwrite what is
          already there.
        </span>
      </div>

      {error ? (
        <div className="flex flex-col gap-1" role="alert">
          <p className="text-content-danger text-sm">{error.message}</p>
          {error.problems.map((p) => (
            <p className="text-content-danger text-xs" key={p}>
              {p}
            </p>
          ))}
        </div>
      ) : null}

      {report ? (
        <>
          <SeedReport report={report} />
          <div>
            <Button onClick={reload} size="sm" variant="secondary">
              Show the staffing book
            </Button>
          </div>
        </>
      ) : null}
    </div>
  );
}

/**
 * What the seed actually created — counts, not a claim of success.
 *
 * The identity warnings are the part worth a reader's attention. cm-api resolves
 * every advisor to ONE key across the whole book; a name that resolves to two
 * stable ids, or an email carrying two, is contradictory source data that it
 * refuses to guess at. Those advisors stay separate, which means a book may be
 * split across two entries for one person — visible here, or not at all.
 */
function SeedReport({ report }: { report: BootstrapReport }) {
  const {
    rosterCreated,
    rosterUpdated,
    configVersion,
    configCreated,
    slotsCreated,
    slotsAlreadyPresent,
    unmatchedSlotKeys,
    identityWarnings,
  } = report;
  const { ambiguousNames, conflictingEmails } = identityWarnings;

  return (
    <div
      className="flex flex-col gap-2 rounded-md border border-border-medium bg-surface-background px-3 py-2 text-sm"
      data-testid="staffing-seed-report"
      role="status"
    >
      <p>
        Seeded from the CRM: <strong>{rosterCreated}</strong>{' '}
        {rosterCreated === 1 ? 'advisor' : 'advisors'} added
        {rosterUpdated > 0 ? `, ${rosterUpdated} updated` : null};{' '}
        {configCreated ? (
          <>
            configuration version <strong>{configVersion}</strong> created
          </>
        ) : (
          <>
            configuration version <strong>{configVersion}</strong> already
            existed and was left alone
          </>
        )}
        ;{' '}
        {slotsCreated > 0 ? (
          <>
            <strong>{slotsCreated}</strong> team{' '}
            {slotsCreated === 1 ? 'seat' : 'seats'} created
          </>
        ) : (
          <>
            {slotsAlreadyPresent} team{' '}
            {slotsAlreadyPresent === 1 ? 'seat' : 'seats'} were already present,
            so none were created
          </>
        )}
        .
      </p>

      {ambiguousNames.length > 0 || conflictingEmails.length > 0 ? (
        <div className="flex flex-col gap-1">
          {ambiguousNames.length > 0 ? (
            <p className="text-content-warning text-xs">
              <strong>{ambiguousNames.length}</strong> display{' '}
              {ambiguousNames.length === 1 ? 'name resolves' : 'names resolve'}{' '}
              to more than one stable id, so those advisors were kept SEPARATE
              rather than merged — two people really can share a name. If one
              person’s book looks split in two, this is why:{' '}
              {ambiguousNames.slice(0, 5).join(', ')}
              {ambiguousNames.length > 5 ? ', …' : null}.
            </p>
          ) : null}
          {conflictingEmails.length > 0 ? (
            <p className="text-content-warning text-xs">
              <strong>{conflictingEmails.length}</strong>{' '}
              {conflictingEmails.length === 1
                ? 'email carries'
                : 'emails carry'}{' '}
              more than one external id — contradictory data in the CRM,
              reported rather than guessed at.
            </p>
          ) : null}
        </div>
      ) : null}

      {unmatchedSlotKeys.length > 0 ? (
        <p className="text-content-danger text-xs">
          <strong>{unmatchedSlotKeys.length}</strong> derived{' '}
          {unmatchedSlotKeys.length === 1 ? 'seat' : 'seats'} named an advisor
          the roster does not hold and{' '}
          {unmatchedSlotKeys.length === 1 ? 'was' : 'were'} skipped. Both
          derivations resolve identities the same way, so this should not happen
          — worth reporting.
        </p>
      ) : null}

      <p className="text-content-secondary text-xs">
        Two things this deliberately did NOT do. <strong>Pins</strong> are not
        seeded — they are keyed by customer name in the source, and resolving
        them to customer ids needs the CRM reconciliation first. And nobody has
        been made <strong>manager</strong>, because that cannot be read from
        customer ownership: a manager may own nothing. Set it on the Team tab.
      </p>
    </div>
  );
}
